'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Copy, Check } from 'lucide-react';
import type { CodeLanguage } from '@/types';
import type React from 'react';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const vscodeDarkPlus = require('react-syntax-highlighter/dist/esm/styles/prism').vscDarkPlus as Record<string, React.CSSProperties>;

// Dynamic import to avoid SSR issues with syntax highlighter
const SyntaxHighlighter = dynamic(
  () => import('react-syntax-highlighter').then((m) => m.Prism),
  {
    ssr: false,
    loading: () => <div className="skeleton h-32 rounded-lg" />,
  }
);

interface Props {
  code: string;
  language: CodeLanguage;
  title?: string;
  highlights?: number[];
  showLineNumbers?: boolean;
}

const languageMap: Record<CodeLanguage, string> = {
  dart: 'dart',
  yaml: 'yaml',
  bash: 'bash',
  json: 'json',
  kotlin: 'kotlin',
  swift: 'swift',
  typescript: 'typescript',
  javascript: 'javascript',
};

export function CodeBlock({ code, language, title, highlights = [], showLineNumbers = true }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{
              background: 'rgba(91,79,232,0.2)',
              color: '#8B85F4',
              fontFamily: 'var(--font-code)',
            }}
          >
            {language}
          </span>
          {title && (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {title}
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-all"
          style={{
            color: copied ? '#00C9A7' : 'var(--color-text-secondary)',
            background: copied ? 'rgba(0,201,167,0.1)' : 'transparent',
          }}
          aria-label="Copy code"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Code */}
      <div style={{ background: '#1E1E1E' }}>
        <SyntaxHighlighter
          language={languageMap[language] ?? 'text'}
          style={vscodeDarkPlus}
          showLineNumbers={showLineNumbers}
          wrapLines={highlights.length > 0}
          lineProps={(lineNumber: number) => ({
            style: highlights.includes(lineNumber)
              ? {
                  background: 'rgba(245, 158, 11, 0.15)',
                  display: 'block',
                  width: '100%',
                }
              : {},
          })}
          customStyle={{
            margin: 0,
            padding: '16px',
            background: 'transparent',
            fontSize: '13px',
            fontFamily: 'var(--font-code)',
            lineHeight: '1.6',
          }}
          lineNumberStyle={{
            color: 'rgba(255,255,255,0.2)',
            minWidth: '2.5em',
            paddingRight: '1em',
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
